/**
 * @file web/app/services/lazada_api_service.js
 * @description Lazada API Service.
 * This service handles interactions with the Lazada API.
 */

import { I } from '../common/v5/config';

/**
 * Lazada API Service
 * Handles interactions with the Lazada API through our backend
 */

export const lazadaApiService = {
/**
   * Get the Lazada authorization URL
   * @param {string} redirectURI - The redirect URI after authorization
   * @returns {Promise<string>} The authorization URL
   */
  getAuthUrl: async (redirectURI, state) => {
    try {
      
      let url = `/admin/lazada/auth?redirect_uri=${encodeURIComponent(redirectURI)}`;
      if (state) {
        url += `&state=${encodeURIComponent(state)}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Auth URL error response:', errorText);
        throw new Error(I('Failed to get authorization URL. Please check your Lazada app configuration.'));
      }
      
      const data = await response.json();
      
      if (!data.auth_url) {
        throw new Error(I('Invalid response from server: no authorization URL received.'));
      }
      
      return data.auth_url;
    } catch (error) {
      console.error('Error getting Lazada auth URL:', error);
      throw error;
    }
  },

  /**
   * Authenticate with Lazada using the authorization code
   * @param {string} code - The authorization code from Lazada
   * @returns {Promise<string>} The access token
   */
  authenticateLazada: async (code, redirectURI) => {
    try {
      if (!code) {
        throw new Error(I('Authorization code is required.'));
      }
      
      const formData = new FormData();
      formData.append('code', code);
      
      // Include redirect_uri if provided, otherwise use default
      const redirectUri = redirectURI || (window.location.origin + '/media/lazada/handletoken');
      formData.append('redirect_uri', redirectUri);

      const response = await fetch('/admin/lazada/auth', {
        method: 'POST',
        body: formData
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Authentication error response:', errorText);
        
        // Try to parse error response
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || I('Authentication failed. Please try again.'));
        } catch {
          throw new Error(I('Authentication failed. Please check your Lazada credentials and try again.'));
        }
      }

      const data = await response.json();
      
      if (!data.access_token) {
        throw new Error(I('No access token received from Lazada. Please try again.'));
      }
      
      // Return both access_token and accountID if available
      return {
        access_token: data.access_token,
        accountID: data.accountID || null
      };
    } catch (error) {
      console.error('Error authenticating with Lazada:', error);
      throw error;
    }
  },

  /**
   * Get long-lived token information
   * @param {string} accessToken - The short-lived access token
   * @returns {Promise<Object>} The token information including account ID
   */
  getTokenInfo: async (accessToken) => {
    try {
      if (!accessToken) {
        throw new Error(I('Access token is required.'));
      }
      
      const formData = new FormData();
      formData.append('access_token', accessToken);

      const response = await fetch('/admin/lazada/token', {
        method: 'POST',
        body: formData
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token info error response:', errorText);
        
        // Try to parse error response
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || I('Failed to get token information.'));
        } catch {
          throw new Error(I('Failed to get token information. Please try again.'));
        }
      }

      const data = await response.json();
      
      // Extract accountID from response - check top level first, then fallback to CountryUserInfo
      let accountID = data.accountID;
      if (!accountID && data.CountryUserInfo && data.CountryUserInfo.length > 0) {
        const info = data.CountryUserInfo[0];
        // Try seller_id first (most reliable), then user_id, then account_id
        accountID = info.seller_id || info.user_id || info.account_id;
      }
      
      if (!accountID) {
        throw new Error(I('No account ID found in token information.'));
      }
      
      return {
        ...data,
        accountID: accountID
      };
    } catch (error) {
      console.error('Error getting Lazada token info:', error);
      throw error;
    }
  },

  /**
   * Test the connection to Lazada API
   * @param {string} accessToken - The access token to test
   * @returns {Promise<boolean>} True if connection is successful
   */
  testConnection: async (accessToken) => {
    try {
      
      const response = await fetch('/admin/lazada/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: accessToken })
      });
      
      if (!response.ok) {
        throw new Error(I('Connection test failed.'));
      }
      
      const data = await response.json();
      return data.success === true;
    } catch (error) {
      console.error('Error testing Lazada connection:', error);
      throw error;
    }
  }
};

/**
 * Fetches product categories from Lazada.
 *
 * @param {string} accessToken - The Lazada access token
 * @param {string} accountID - The Lazada Seller ID
 * @returns {Promise<object>} A promise that resolves with a list of product categories
 */
export const fetchLazadaProductCategories = (accessToken, accountID) => {
  return new Promise((resolve, reject) => {
    fetch('/admin/account/lazada/categories', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        resolve({
          success: true,
          categories: data.categories,
        });
      } else {
        reject({
          success: false,
          message: data.message || I("Failed to fetch Lazada product categories."),
        });
      }
    })
    .catch(error => {
      reject({
        success: false,
        message: error.message || I("Failed to connect to Lazada service."),
        });
    });
  });
};

/**
 * Simulates fetching products for a specific category from Lazada.
 *
 * @param {string} accessToken - 
 * @param {string} accountID - The Lazada Seller ID.
 * @param {string} categoryId - The ID of the category to fetch products for.
 * @returns {Promise<object>} A promise that resolves with a list of sample products.
 */
export const fetchLazadaProducts = (accessToken, accountID, categoryId) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (accessToken && accessToken.startsWith('dummy_lazada_access_token_') && accountID && categoryId) {
        console.log(`[LazadaApiService] Fetching products for SellerID: ${accountID}, CategoryID: ${categoryId}`);
        let sampleProducts = [];
        switch (categoryId) {
          case 'cat_elec_001':
            sampleProducts = [
              { id: 'prod_elec_101', name: I("Smart LED TV 55 inch"), description: I("Ultra HD, HDR, Smart Hub"), price: "499.99", currency: "USD", stock: 50, images: ['/img/sample_tv.png', '/img/sample_tv_angle.png'], categoryId: 'cat_elec_001' },
              { id: 'prod_elec_102', name: I("Wireless Noise-Cancelling Headphones"), description: I("Bluetooth, Over-ear, 20-hour battery"), price: "149.50", currency: "USD", stock: 120, images: ['/img/sample_headphones.png'], categoryId: 'cat_elec_001' },
            ];
            break;
          case 'cat_fas_002':
            sampleProducts = [
              { id: 'prod_fas_201', name: I("Men's Casual T-Shirt"), description: I("Cotton, Slim Fit, Various Colors"), price: "19.99", currency: "USD", stock: 200, images: ['/img/sample_tshirt.png'], categoryId: 'cat_fas_002' },
              { id: 'prod_fas_202', name: I("Women's Summer Dress"), description: I("Floral Print, Maxi Length"), price: "39.00", currency: "USD", stock: 80, images: ['/img/sample_dress.png'], categoryId: 'cat_fas_002' },
            ];
            break;
          case 'cat_hom_003':
            sampleProducts = [
              { id: 'prod_hom_301', name: I("Modern Coffee Table"), description: I("Oak wood, minimalist design"), price: "120.00", currency: "USD", stock: 30, images: ['/img/sample_table.png'], categoryId: 'cat_hom_003' },
            ];
            break;
          default:
            sampleProducts = [
              { id: 'prod_gen_000', name: I("Generic Sample Product"), description: I("This is a default sample product."), price: "9.99", currency: "USD", stock: 10, images: [], categoryId: categoryId }
            ];
        }
        resolve({
          success: true,
          products: sampleProducts,
        });
      } else {
        console.warn('[LazadaApiService] Fetching products failed. Invalid token, missing Seller ID, or Category ID.');
        reject({
          success: false,
          message: I("Failed to fetch Lazada products (mock). Invalid access token, Seller ID, or Category ID."),
        });
      }
    }, MOCK_API_DELAY);
  });
};

